import { tokenDeRepasseUtilizavel } from '@/utils/Auth';

/**
 * A marca de SSO na URL: `?sso=1`.
 *
 * E o contrato entre o Sapron e as apps para onde ele manda (Wallet,
 * Franquias), e da Wallet de volta ao Sapron, quando a sessao e do IDP. Nenhum token atravessa a URL; a marca so diz
 * "quem mandou para ca tinha sessao do IDP", e quem recebe tenta `prompt=none`
 * contra a sessao SSO do Keycloak antes de decidir pelo login. Sem sessao SSO
 * o Keycloak responde `login_required` e a pessoa cai no login normal.
 *
 * - Sapron → Wallet: `<wallet>/?sso=1`; o middleware da Wallet inicia o login
 *   OIDC silencioso.
 * - Sapron → Franquias (Host/CoHost com a flag): `<franquias>/?sso=1`; o
 *   Franquias tenta o mesmo login silencioso antes do proprio login.
 * - Wallet → Sapron: `<sapron>/parceiros/painel?sso=1`; o `IdpAuthProvider`
 *   tenta restaurar a sessao antes de o `RequireAuth` mandar para `/login`.
 */
export const SSO_PARAM = 'sso';

const CHAVES_DO_REPASSE = [SSO_PARAM, 'token', 'target'];

/** A URL com a marca, preservando o que ja houver na query. */
export function comMarcaDeSso(url: string): string {
  const separador = url.includes('?') ? '&' : '?';
  return `${url}${separador}${SSO_PARAM}=1`;
}

interface LocalDaUrl {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/**
 * Se a pagina abriu com a marca de SSO.
 *
 * Tambem conta como marca o repasse legado da Wallet para o parceiro
 * (`#target=partner`) sem um token utilizavel: e o que a Wallet manda hoje
 * quando o parceiro entrou nela pelo IDP, e o que ela mandava antes de conhecer
 * a marca.
 */
export function chegouComMarcaDeSso({ search, hash }: LocalDaUrl): boolean {
  const query = new URLSearchParams(search);
  if (query.get(SSO_PARAM) === '1') return true;

  const fragmento = new URLSearchParams(hash.replace(/^#/, ''));
  const target = fragmento.get('target') ?? query.get('target');
  const token = fragmento.get('token') || query.get('token');
  return target === 'partner' && !tokenDeRepasseUtilizavel(token);
}

/** A rota atual sem a marca e sem as chaves do repasse, para o `returnTo`. */
export function rotaSemMarcaDeSso({ pathname, search, hash }: LocalDaUrl): string {
  const query = new URLSearchParams(search);
  CHAVES_DO_REPASSE.forEach((chave) => query.delete(chave));
  const fragmento = new URLSearchParams(hash.replace(/^#/, ''));
  const hashDoRepasse = CHAVES_DO_REPASSE.some((chave) => fragmento.has(chave));

  const resto = query.toString();
  return `${pathname}${resto ? `?${resto}` : ''}${hashDoRepasse ? '' : hash}`;
}
